import { Dimensions, Metrics, type BreakdownRow } from '@/api/types';
import { resolveMetric } from '@/api/metric-resolution';
import { BarList } from '@/components/BarList';
import { ExportButton } from '@/components/ExportButton';
import { ResolvedTile } from '@/components/ResolvedTile';
import { TimeOnPage } from '@/components/TimeOnPage';
import { RangeBar } from '@/components/RangeBar';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { breakdown, rowValue, toBars, useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError, noSourceSentence } from '@/components/AnalyticsLoadError';

/**
 * Per-page behaviour: which pages people land on, and how far they actually read.
 */
export function PagesPage({ siteKey }: { siteKey: string }) {
  const { data, isLoading, isError, error, days, setDays, from, to, setRange, isCustomRange } = useOverview(siteKey);

  const pages = breakdown(data, Dimensions.pagePath);
  const titles = breakdown(data, Dimensions.pageTitle);
  const totals = data?.totals ?? {};

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={data}
        setRange={setRange} isCustomRange={isCustomRange}
        actions={
          <ExportButton
            siteKey={siteKey} from={from} to={to}
            dimension={Dimensions.pagePath} label="Export pages"
          />
        }
      />

      {isLoading && <div className="empty-state"><span className="spinner" /></div>}
      {isError && <AnalyticsLoadError error={error} what="pages" notFound={noSourceSentence(siteKey)} />}

      {data && (
        <>
          <div className="kpi-row">
            <ResolvedTile label="Page views" totals={totals} chain="pageViews" />

            {/* Pages per session is DERIVED from GA4 rather than read from Clarity.
                Clarity publishes `pagesPerSessionPercentage`, but taking it here would mix
                providers inside one KPI row: it is computed over Clarity's session count, which
                includes bots GA4 filters out, so it would silently disagree with the Page views
                and Sessions tiles beside it. Dividing GA4 by GA4 keeps the row internally
                consistent. Falls back to Clarity's own figure only when GA4 has nothing. */}
            <PagesPerSessionTile totals={totals} />

            {/* Clarity-only: GA4 has no scroll-depth metric without custom events. */}
            <ScrollDepthTile depth={totals[Metrics.averageScrollDepth]} />

            {/* GA4's engagement duration wins; Clarity's engagement time is the fallback. */}
            <ResolvedTile label="Avg engagement" totals={totals} chain="engagement" sub="seconds" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            <div className="card">
              <div className="card-header">
                <h2 className="t-heading" style={{ margin: 0 }}>Top pages</h2>
              </div>
              <div className="card-body">
                <BarList rows={toBars(pages)} limit={12} emptyLabel="No page-level rows yet." />
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="t-heading" style={{ margin: 0 }}>Page titles</h2>
              </div>
              <div className="card-body">
                <BarList rows={toBars(titles)} limit={12} emptyLabel="No page titles reported yet." />
              </div>
            </div>
          </div>

          {/* The question people come to this tab to ask. Above the raw counts, because
              "how long did they stay" is what the counts are usually a proxy for. */}
          <TimeOnPage siteKey={siteKey} from={from} to={to} />

          {pages.length > 0 && <PageDetailTable rows={pages} />}
        </>
      )}
    </>
  );
}

/**
 * Scroll depth is a ratio against a natural limit (100%), so it renders as a METER, not a bar
 * chart. The unfilled track is a lighter step of the same hue so the state reads across the
 * whole bar rather than only where it is filled.
 */
/**
 * Pages per session, computed from GA4 rather than taken from Clarity.
 *
 * Both providers can answer this, and they answer differently — Clarity's denominator includes
 * bot sessions that GA4 discards. Reading Clarity's figure into a row whose other tiles are GA4
 * produces an internally inconsistent KPI row, so this divides GA4 by GA4 and only falls back to
 * Clarity's own number when GA4 supplied neither input.
 */
function PagesPerSessionTile({ totals }: { totals: Record<string, number> }) {
  const views = resolveMetric(totals, 'pageViews');
  const sessions = resolveMetric(totals, 'sessions');

  const derivable =
    views?.source === 'ga4' && sessions?.source === 'ga4' && sessions.value > 0;

  if (derivable) {
    return (
      <div className="card stat-tile">
        <div className="stat-label t-label">Pages per session</div>
        <div className="stat-value">{(views!.value / sessions!.value).toFixed(2)}</div>
        <div className="stat-sub t-caption">Google Analytics</div>
      </div>
    );
  }

  const clarity = totals[Metrics.pagesPerSession];
  return (
    <StatTile
      label="Pages per session"
      value={clarity ?? null}
      sub={clarity !== undefined ? 'Clarity (GA4 unavailable)' : undefined}
    />
  );
}

function ScrollDepthTile({ depth }: { depth: number | undefined }) {
  if (depth === undefined) {
    return <StatTile label="Avg scroll depth" value={null} sub="Clarity" />;
  }

  const pct = Math.max(0, Math.min(100, depth));

  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">Avg scroll depth</div>
      <div className="stat-value">{pct.toFixed(0)}%</div>
      <div
        style={{
          marginTop: 8,
          height: 6,
          borderRadius: 3,
          background: 'var(--primary-soft)',
          overflow: 'hidden',
        }}
        role="meter"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Average scroll depth"
      >
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--series-1)' }} />
      </div>
      <div className="stat-sub t-caption">Clarity</div>
    </div>
  );
}

/** The table view — the relief channel that guarantees every value is readable as text. */
function PageDetailTable({ rows }: { rows: BreakdownRow[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>All pages</h2>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Page</th>
            <th className="num">Sessions</th>
            <th className="num">Views</th>
            <th className="num">Rage</th>
            <th className="num">Dead</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((row) => (
            <tr key={row.value}>
              <td style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.value || '(not set)'}
              </td>
              <td className="num">{formatCompact(rowValue(row))}</td>
              <td className="num">{fmt(row.metrics[Metrics.screenPageViews])}</td>
              <td className="num">{fmt(row.metrics[Metrics.rageClickCount])}</td>
              <td className="num">{fmt(row.metrics[Metrics.deadClickCount])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmt(value: number | undefined): string {
  return value === undefined ? '—' : formatCompact(value);
}
